/**
 * Provision the native "Share to Lapo" OS share-sheet handler (mobile Tauri only). The Android share
 * Activity and the iOS Share Extension run in a SEPARATE process from the WebView, so they can't reuse
 * its Firebase session. Once signed in we mint a long-lived, self-issued bearer token (POST
 * /me/share-token) and hand it — plus the API base URL — to the Rust `store_share_credentials` command,
 * which writes it where the handler reads it (Android app-data dir / iOS App Group container).
 *
 * No-op on the web and on desktop (no OS share handler there). Provisioning is recorded per-user in
 * localStorage so we mint once per user per install rather than on every launch; when a DIFFERENT user
 * signs in on the same device we re-mint (which supersedes the previous token server-side), so shared
 * content always lands in the currently-signed-in account.
 */
import { isTauri, isMobile } from './shell/shell.js';

export interface ShareTokenMinter {
  getShareToken(): Promise<{ token: string }>;
}

const PROVISIONED_KEY = 'lapo-share-provisioned-uid';

/**
 * Mint + store the share credentials if this user hasn't been provisioned on this install yet. Safe to
 * call on every sign-in; guarded so it does real work only once per (user, install).
 */
type MarkerStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export async function initShareTarget(
  api: ShareTokenMinter,
  apiBase: string,
  userId: string,
  onError: (err: unknown) => void = () => {},
  storage: MarkerStore = localStorage,
): Promise<void> {
  if (!isTauri() || !isMobile() || !apiBase || !userId) return;
  if (storage.getItem(PROVISIONED_KEY) === userId) return;
  try {
    const { token } = await api.getShareToken();
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('store_share_credentials', { token, apiBase });
    storage.setItem(PROVISIONED_KEY, userId);
  } catch (err) {
    onError(err);
  }
}

/** Forget the provisioning marker on sign-out so the next user re-provisions their own token. */
export function resetShareTargetProvisioning(storage: MarkerStore = localStorage): void {
  try {
    storage.removeItem(PROVISIONED_KEY);
  } catch {
    /* best-effort */
  }
}
